import { useMemo, useState } from 'react';
import { Tag, Tooltip } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { orderBy } from 'lodash';
import { useOutletContext } from 'react-router-dom';
import Table from 'components/ui/base/table/Table';
import { H5 } from 'components/ui/base/typography';
import Button from 'components/new-ui/base/button/Button';
import { dateStandardFormat, getHour } from 'core/utils/date';
import {
    ICS2ArrivalHistoryEntry,
    ICS2ArrivalHistoryType,
    ICS2ArrivalNotificationMessage,
    ICS2ArrivalView,
} from 'store/declarations/eu/ics2/arrival/ics2-arrival-notification-declaration';
import { FlexDiv, SpanEllipsis } from 'views/custom-declaration/declaration-table/components/DeclarationsTable.styled';
import {
    HistoryStatusContainer,
    HistoryViewContainer,
} from 'views/declarations/declaration-tabs/history/components/styledComponents';
import ArrivalMessagesDrawer from './ArrivalMessagesDrawer';
import { ArrivalNotificationHistoryOutletContext } from './ArrivalNotificationHistory';
import { belongsToCurrentAttempt, ignoredReason } from './arrivalMessageUtils';

const actionLabels: Record<ICS2ArrivalHistoryType, string> = {
    ARRIVAL_CREATED: 'Created',
    ARRIVAL_DRAFT_REOPENED: 'Draft reopened',
    ARRIVAL_SUBMITTED: 'Submitted',
    ARRIVAL_DELIVERY_CONFIRMED: 'Delivery confirmed',
    ARRIVAL_DISPATCH_ATTENTION: 'Dispatch needs attention',
    ARRIVAL_DISPATCH_RECOVERED: 'Dispatch recovered',
    ARRIVAL_REGISTERED: 'Registered',
    ARRIVAL_REJECTED: 'Rejected',
    ARRIVAL_INCORRECT_STATE: 'Incorrect state',
    ARRIVAL_CONTROL_INSTRUCTION: 'Control instruction',
    ARRIVAL_NOTIFICATION_IGNORED: 'Notification ignored',
    ENS_ARRIVAL_CONFIRMED: 'ENS arrival confirmed',
    ARRIVAL_CONSULTATION_RESULT: 'Consultation result',
};

/** What the service means by each reason it records for a message it did not act on. */
const ignoredExplanations: Record<string, string> = {
    SUPERSEDED_ATTEMPT: 'It answers a submission that a later one replaced.',
    REJECTED_ATTEMPT: 'The submission it answers had already been rejected.',
    INACTIVE_ATTEMPT: 'The arrival was no longer in a state this could apply to.',
};

/**
 * Whether a customs message spoke for the arrival. Only an IE3N07 or IE3N08 is marked by the service
 * when it is not acted on; a rejection of a replaced attempt carries no marker, so it is recognised
 * by the attempt it answers.
 */
const MessageStatusTag = ({
    message,
    arrival,
}: {
    message: ICS2ArrivalNotificationMessage;
    arrival: ICS2ArrivalView | undefined;
}) => {
    const reason = ignoredReason(message);
    if (reason)
        return (
            <Tooltip title={ignoredExplanations[reason] ?? reason}>
                <Tag>Not applied</Tag>
            </Tooltip>
        );

    if (!belongsToCurrentAttempt(message, arrival))
        return (
            <Tooltip title="It answers an earlier submission of this arrival.">
                <Tag>Superseded</Tag>
            </Tooltip>
        );

    return <Tag color="blue">Applied</Tag>;
};

const textOrDash = (text: string | undefined) => <SpanEllipsis>{text || '-'}</SpanEllipsis>;

/**
 * Everything that happened to this arrival, newest first: what the operator did, what the dispatch
 * did and what customs answered.
 *
 * The registration state sits here rather than in the header because it is the answer to the
 * submission rather than a property of the form: the MRN and the registration time only exist once
 * customs has replied, well after submitting returns.
 */
const ArrivalNotificationHistoryTable = () => {
    const { arrival, messages, history } = useOutletContext<ArrivalNotificationHistoryOutletContext>();
    const [openMessage, setOpenMessage] = useState<ICS2ArrivalNotificationMessage | undefined>();

    const newestFirst = useMemo(() => orderBy(history, 'createdAt', 'desc'), [history]);

    const messagesByUuid = useMemo(
        () => new Map(messages.map((message) => [message.messageUuid, message])),
        [messages]
    );

    const columns: ColumnsType<ICS2ArrivalHistoryEntry> = [
        {
            title: 'Date',
            dataIndex: 'createdAt',
            render: (createdAt: string | undefined) => {
                if (!createdAt) return textOrDash(undefined);

                const date = new Date(createdAt);
                return (
                    <FlexDiv>
                        <span>{getHour(date)}</span>
                        <span>{dateStandardFormat(date)}</span>
                    </FlexDiv>
                );
            },
        },
        {
            title: 'User',
            dataIndex: 'creatorName',
            render: textOrDash,
        },
        {
            title: 'Action',
            dataIndex: 'type',
            render: (type: ICS2ArrivalHistoryType) => textOrDash(actionLabels[type] ?? type),
        },
        {
            title: 'Description',
            dataIndex: 'description',
            render: textOrDash,
        },
        {
            title: 'Transaction Id',
            dataIndex: 'transactionId',
            render: textOrDash,
        },
        {
            title: 'Status',
            key: 'status',
            render: (_, entry) => {
                const message = entry.messageUuid ? messagesByUuid.get(entry.messageUuid) : undefined;
                return message ? <MessageStatusTag message={message} arrival={arrival} /> : textOrDash(undefined);
            },
        },
        {
            title: '',
            key: 'details',
            align: 'right',
            render: (_, entry) => {
                const message = entry.messageUuid ? messagesByUuid.get(entry.messageUuid) : undefined;
                if (!message) return null;

                return (
                    <Button type="link" style={{ color: 'var(--brand-6)' }} onClick={() => setOpenMessage(message)}>
                        See Details
                    </Button>
                );
            },
        },
    ];

    return (
        <HistoryViewContainer>
            <HistoryStatusContainer style={{ padding: '2rem' }}>
                <H5 style={{ marginBottom: 24 }}>Status History</H5>
                <Table rowKey="id" pagination={false} columns={columns} dataSource={newestFirst} />
            </HistoryStatusContainer>

            <ArrivalMessagesDrawer message={openMessage} onClose={() => setOpenMessage(undefined)} />
        </HistoryViewContainer>
    );
};

export default ArrivalNotificationHistoryTable;
